import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowRight,
  CheckCircle2,
  LayoutDashboard,
  Loader2,
  PartyPopper,
  Rocket,
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { cn } from '@/lib/utils';
import { useOnboardingStatus } from '@/hooks/onboarding/useOnboardingStatus';
import type { OnboardingTaskStatus } from '@/types/onboarding';

export const Route = createFileRoute('/_dashboard/dashboard/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage() {
  const { data, isLoading, isError } = useOnboardingStatus();

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:py-12">
      <header className="mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <Rocket size={13} />
          Getting started
        </div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Welcome to Notifi</h1>
        <p className="mt-2 text-muted-foreground">
          A few quick steps to set up your account and go live. Finish these and your dashboard
          takes over from here.
        </p>
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 size={16} className="animate-spin" />
          Loading your setup checklist…
        </div>
      )}

      {isError && (
        <Card className="p-6 text-sm text-muted-foreground">
          We couldn&apos;t load your setup checklist right now. Please refresh the page to try
          again.
        </Card>
      )}

      {data && (
        <>
          <ProgressHeader completed={data.completedCount} total={data.totalCount} />

          <ol className="mt-6 space-y-3">
            {data.tasks.map((task, index) => (
              <TaskRow key={task.key} task={task} index={index} />
            ))}
          </ol>

          {data.complete ? (
            <Card className="mt-8 flex flex-col items-start gap-4 border-green-200 bg-green-50 p-6 sm:flex-row sm:items-center sm:justify-between dark:border-green-900/50 dark:bg-green-950/30">
              <div className="flex items-start gap-3">
                <PartyPopper className="mt-0.5 shrink-0 text-green-600" size={20} />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-200">
                    You&apos;re all set!
                  </p>
                  <p className="text-sm text-green-800/80 dark:text-green-300/70">
                    Your account is ready. Head to your dashboard to start working.
                  </p>
                </div>
              </div>
              <Link
                to="/dashboard/home"
                className={cn(buttonVariants({ variant: 'default' }), 'shrink-0')}
              >
                <LayoutDashboard size={16} />
                Go to dashboard
              </Link>
            </Card>
          ) : (
            <div className="mt-8 flex items-center justify-between border-t pt-6">
              <p className="text-sm text-muted-foreground">
                You can explore your dashboard anytime — we&apos;ll keep this checklist here until
                it&apos;s done.
              </p>
              <Link
                to="/dashboard/home"
                className={cn(
                  buttonVariants({ variant: 'ghost' }),
                  'shrink-0 text-muted-foreground',
                )}
              >
                Go to dashboard
                <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProgressHeader({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-medium">Your setup</span>
        <span className="text-muted-foreground">
          {completed} of {total} complete
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function TaskRow({ task, index }: { task: OnboardingTaskStatus; index: number }) {
  return (
    <li>
      <Card
        className={cn(
          'flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between',
          task.done && 'bg-muted/40',
        )}
      >
        <div className="flex items-start gap-4">
          <StatusBadge done={task.done} index={index} />
          <div>
            <p className={cn('font-medium', task.done && 'text-muted-foreground line-through')}>
              {task.title}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>
          </div>
        </div>

        <div className="shrink-0 sm:pl-4">
          {task.done ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600">
              <CheckCircle2 size={16} />
              Done
            </span>
          ) : (
            <Button asChild size="sm">
              <Link to={task.href}>
                Set up
                <ArrowRight size={16} />
              </Link>
            </Button>
          )}
        </div>
      </Card>
    </li>
  );
}

function StatusBadge({ done, index }: { done: boolean; index: number }) {
  if (done) {
    return <CheckCircle2 className="mt-0.5 shrink-0 text-green-600" size={22} />;
  }
  return (
    <span className="mt-0.5 flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 border-muted-foreground/30 text-xs font-semibold text-muted-foreground">
      {index + 1}
    </span>
  );
}
